import React, { useState } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Key, AlertTriangle, Check } from 'lucide-react';

export const ChangePasswordPage = () => {
  const { changePassword, requiresPasswordReset } = useAdminAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validatePassword = (pass) => {
    const checks = {
      length: pass.length >= 12,
      upper: /[A-Z]/.test(pass),
      lower: /[a-z]/.test(pass),
      number: /[0-9]/.test(pass),
      special: /[!@#$%^&*]/.test(pass)
    };
    return checks;
  };

  const checks = validatePassword(newPassword);
  const isValid = Object.values(checks).every(Boolean) && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Please ensure all password requirements are met');
      return;
    }

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      // Success - AdminAuthProvider will handle state update
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center">
            <Key className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">
            {requiresPasswordReset ? 'Change Your Password' : 'Update Password'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {requiresPasswordReset 
              ? 'You must change your password before continuing.' 
              : 'Update your admin password'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-900/50 border-red-800">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!requiresPasswordReset && (
              <div className="space-y-2">
                <Label htmlFor="current" className="text-gray-300">Current Password</Label>
                <Input
                  id="current"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required={!requiresPasswordReset}
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="current-password"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="new" className="text-gray-300">New Password</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="new-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-gray-300">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="confirm-password"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-700/50 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-400 mb-2">Password requirements:</p>
              <Requirement met={checks.length} text="At least 12 characters" />
              <Requirement met={checks.upper} text="One uppercase letter" />
              <Requirement met={checks.lower} text="One lowercase letter" />
              <Requirement met={checks.number} text="One number" />
              <Requirement met={checks.special} text="One special character (!@#$%^&*)" />
              <Requirement met={newPassword === confirmPassword && newPassword.length > 0} text="Passwords match" />
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={loading || !isValid}
              data-testid="change-password-submit"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const Requirement = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-400' : 'text-gray-500'}`}>
    {met ? <Check className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-gray-500" />}
    {text}
  </div>
);

export default ChangePasswordPage;
